import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Ship, Users, Wallet, Upload, Search, ArrowUpRight, ArrowDownRight,
  Sparkles, FileCheck2, PackageOpen, PackageCheck, AlertTriangle,
  ChevronRight, Landmark, Coins, TrendingUp, Circle, FileText, Truck,
} from 'lucide-react';
import { api, useAuth } from '../contexts/AuthContext';
import ErpShell from '../components/ErpShell';

const fmtMoney = (v, code = '₪') => `${code}${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtRel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  const diff = Math.round((now - d) / (1000 * 60));
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m`;
  if (diff < 60 * 24) return `${Math.floor(diff / 60)}h`;
  return `${Math.floor(diff / (60 * 24))}d`;
};

const KpiCard = ({ label, value, sub, delta, onClick, gradient, icon: Icon, testid }) => (
  <motion.button
    onClick={onClick}
    data-testid={testid}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
    className="text-start w-full gg-card gg-card-hover p-5 relative overflow-hidden"
  >
    <div
      className="absolute -top-16 -end-16 w-40 h-40 rounded-full opacity-40 pointer-events-none"
      style={{ background: gradient || 'radial-gradient(circle, rgba(201,162,39,0.4), transparent 70%)', filter: 'blur(20px)' }}
    />
    <div className="flex items-start justify-between relative">
      <div>
        <p className="gg-eyebrow">{label}</p>
        <p className="gg-kpi-value mt-3 gg-tabular">{value}</p>
        <div className="flex items-center gap-2 mt-2">
          {delta && (
            <span className={'gg-chip ' + (delta.up ? 'gg-chip-emerald' : 'gg-chip-accent')} style={{ padding: '1px 8px', fontSize: 10 }}>
              {delta.up ? <ArrowUpRight size={9} /> : <ArrowDownRight size={9} />} {delta.text}
            </span>
          )}
          {sub && <span className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>{sub}</span>}
        </div>
      </div>
      {Icon && (
        <span
          className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: 'var(--gg-accent-soft)', color: 'var(--gg-accent)' }}
        >
          <Icon size={17} />
        </span>
      )}
    </div>
  </motion.button>
);

const QuickAction = ({ label, icon: Icon, onClick, primary }) => (
  <button
    onClick={onClick}
    className={primary ? 'gg-btn-primary' : 'gg-btn-secondary'}
    data-testid={`quick-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <Icon size={14} /> <span>{label}</span>
  </button>
);

export default function ErpDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [trackings, setTrackings] = useState([]);
  const [bankData, setBankData] = useState({ balance: 0, transactions: [] });
  const [cashData, setCashData] = useState({ balance: 0, transactions: [] });
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, s, b, ca, a] = await Promise.all([
          api.get('/customers').catch(() => ({ data: { customers: [] } })),
          api.get('/shipment-tracking').catch(() => ({ data: { shipments: [] } })),
          api.get('/financial-tracker/bank').catch(() => ({ data: { transactions: [], balance: 0 } })),
          api.get('/financial-tracker/cash').catch(() => ({ data: { transactions: [], balance: 0 } })),
          api.get('/alerts').catch(() => ({ data: { items: [] } })),
        ]);
        setCustomers(c.data.customers || []);
        setTrackings(s.data.shipments || []);
        setBankData(b.data || { balance: 0, transactions: [] });
        setCashData(ca.data || { balance: 0, transactions: [] });
        setAlerts(a.data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const stats = useMemo(() => {
    const shipments = trackings || [];
    const onTheWay = shipments.filter((s) => s.status === 'ON THE WAY').length;
    const delivered = shipments.filter((s) => s.status === 'DELIVERED').length;
    const clearanceDelay = shipments.filter((s) => s.status === 'CLEARANCE DELAY' || s.status === 'ON HOLD').length;
    const openAlerts = (alerts || []).filter((a) => !a.dismissed).length;
    return {
      totalShipments: shipments.length,
      onTheWay,
      delivered,
      clearanceDelay,
      openAlerts,
      bankBalance: bankData.balance || 0,
      cashBalance: cashData.balance || 0,
      totalBalance: (bankData.balance || 0) + (cashData.balance || 0),
    };
  }, [trackings, bankData, cashData, alerts]);

  const insights = useMemo(() => {
    const list = [];
    if (stats.clearanceDelay > 0) list.push({ text: `${stats.clearanceDelay} shipment${stats.clearanceDelay > 1 ? 's are' : ' is'} stuck in clearance`, tone: 'warning' });
    if (stats.onTheWay > 5) list.push({ text: `${stats.onTheWay} shipments in transit — check ETAs`, tone: 'accent' });
    if (stats.openAlerts) list.push({ text: `${stats.openAlerts} alert${stats.openAlerts > 1 ? 's' : ''} need attention`, tone: 'danger' });
    if (!list.length) list.push({ text: 'Everything is calm. No urgent items right now.', tone: 'emerald' });
    return list;
  }, [stats]);

  const recentActivity = useMemo(() => {
    const acts = [];
    (trackings || []).slice(0, 6).forEach((s) => acts.push({
      icon: s.shipmentType === 'AIR' ? PackageCheck : PackageOpen,
      title: s.trackingNumber || 'Shipment',
      sub: `${s.shipmentType || '—'} · ${s.status || '—'}`,
      customer: s.owner || s.location || '—',
      time: s.updatedAt || s.createdAt,
      color: s.status === 'DELIVERED' ? 'var(--gg-emerald)' : (s.status === 'CLEARANCE DELAY' ? 'var(--gg-danger)' : 'var(--gg-accent)'),
    }));
    const txns = [...(bankData.transactions || []), ...(cashData.transactions || [])].slice(0, 4);
    txns.forEach((p) => acts.push({
      icon: p.transactionType === 'income' ? ArrowDownRight : ArrowUpRight,
      title: p.description || p.category || 'Transaction',
      sub: `${p.transactionType === 'income' ? 'Received' : 'Paid'} — ${p.category || ''}`,
      customer: fmtMoney(p.amount),
      time: p.date || p.createdAt,
      color: p.transactionType === 'income' ? 'var(--gg-emerald)' : 'var(--gg-accent)',
    }));
    return acts.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0)).slice(0, 7);
  }, [trackings, bankData, cashData]);

  const greetingHour = new Date().getHours();
  const greeting = greetingHour < 12 ? 'Good morning' : greetingHour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (user?.name || 'there').split(' ')[0];
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <ErpShell title="Dashboard">
      {/* HERO */}
      <section className="pb-8 relative">
        <p className="gg-eyebrow" style={{ color: 'var(--gg-accent)' }}>{today}</p>
        <div className="flex items-end justify-between mt-3 flex-wrap gap-6">
          <h1 className="gg-hero-title" data-testid="dashboard-greeting">
            {greeting},<br />
            <span className="text-gradient-orange">Gerizim.</span>
          </h1>
          <div className="flex flex-wrap gap-2">
            <QuickAction label="New Shipment" icon={Ship} onClick={() => navigate('/erp/shipments')} primary />
            <QuickAction label="New Customer" icon={Users} onClick={() => navigate('/erp/customers')} />
            <QuickAction label="Add Payment" icon={Wallet} onClick={() => navigate('/erp/finance')} />
            <QuickAction label="Search" icon={Search} onClick={() => document.querySelector('[data-testid=erp-search-trigger]')?.click()} />
          </div>
        </div>
      </section>

      <OverdueBanner />
      <TrackingBanner />

      {/* KPI CARDS */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          testid="kpi-shipments"
          label="Active Shipments"
          value={stats.onTheWay}
          sub={`${stats.totalShipments} total`}
          icon={Ship}
          gradient="radial-gradient(circle, rgba(201,162,39,0.35), transparent 70%)"
          onClick={() => navigate('/erp/shipments')}
        />
        <KpiCard
          testid="kpi-delivered"
          label="Delivered"
          value={stats.delivered}
          sub="completed this period"
          icon={PackageCheck}
          gradient="radial-gradient(circle, rgba(52,199,89,0.3), transparent 70%)"
          delta={{ up: true, text: 'on track' }}
          onClick={() => navigate('/erp/shipments')}
        />
        <KpiCard
          testid="kpi-treasury"
          label="Treasury"
          value={fmtMoney(stats.totalBalance)}
          sub={`Bank ${fmtMoney(stats.bankBalance)} · Cash ${fmtMoney(stats.cashBalance)}`}
          icon={Wallet}
          gradient="radial-gradient(circle, rgba(0,122,255,0.28), transparent 70%)"
          onClick={() => navigate('/erp/finance')}
        />
        <KpiCard
          testid="kpi-customers"
          label="Customers"
          value={customers.length}
          sub={`${stats.openAlerts} alerts open`}
          icon={Users}
          gradient="radial-gradient(circle, rgba(175,82,222,0.28), transparent 70%)"
          onClick={() => navigate('/erp/customers')}
        />
      </section>

      {/* MAIN BODY */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Recent Activity feed */}
        <div className="lg:col-span-2 gg-card p-6" data-testid="recent-activity">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <p className="gg-eyebrow">Live feed</p>
              <h2 className="gg-section-title mt-1" style={{ fontSize: 20 }}>Shipments in motion</h2>
            </div>
            <button onClick={() => navigate('/erp/shipments')} className="gg-btn-ghost text-xs">
              All shipments <ChevronRight size={12} />
            </button>
          </div>
          {loading ? (
            <div className="space-y-3 py-8">
              {[1,2,3,4].map((i) => (
                <div key={i} className="h-14 rounded-xl animate-pulse" style={{ background: 'var(--gg-surface-2)' }} />
              ))}
            </div>
          ) : recentActivity.length === 0 ? (
            <p className="text-sm text-center py-10" style={{ color: 'var(--gg-text-dim)' }}>Nothing to report yet.</p>
          ) : (
            <div className="space-y-1">
              {recentActivity.map((a, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  className="flex items-center gap-4 py-3 px-2 rounded-xl transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
                  data-testid={`activity-row-${i}`}
                >
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--gg-surface-2)', color: a.color }}>
                    <a.icon size={15} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate gg-tabular" style={{ color: 'var(--gg-text)' }}>{a.title}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--gg-text-dim)' }}>{a.sub}</p>
                  </div>
                  <div className="text-end shrink-0">
                    <p className="text-xs font-medium truncate" style={{ color: 'var(--gg-text)' }}>{a.customer}</p>
                    <p className="text-[11px] gg-mono" style={{ color: 'var(--gg-text-mute)' }}>{fmtRel(a.time)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {/* Money panel */}
          <div className="gg-card p-6" data-testid="money-panel">
            <p className="gg-eyebrow">Treasury</p>
            <h2 className="gg-section-title mt-1" style={{ fontSize: 18 }}>Where the money lives</h2>

            <div className="mt-5 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--gg-info-soft)', color: 'var(--gg-info)' }}>
                    <Landmark size={16} />
                  </span>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>Bank of Palestine</p>
                    <p className="gg-tabular" style={{ fontSize: 20, color: 'var(--gg-text)', fontWeight: 700 }}>{fmtMoney(stats.bankBalance)}</p>
                  </div>
                </div>
                <span className="gg-chip gg-chip-info">ILS</span>
              </div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--gg-emerald-soft)', color: 'var(--gg-emerald)' }}>
                    <Coins size={16} />
                  </span>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>Office Cash</p>
                    <p className="gg-tabular" style={{ fontSize: 20, color: 'var(--gg-text)', fontWeight: 700 }}>{fmtMoney(stats.cashBalance)}</p>
                  </div>
                </div>
                <span className="gg-chip gg-chip-emerald">ILS</span>
              </div>
              <div className="pt-3 flex items-start justify-between" style={{ borderTop: '1px solid var(--gg-border)' }}>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--gg-accent-soft)', color: 'var(--gg-accent)' }}>
                    <TrendingUp size={16} />
                  </span>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--gg-text-dim)' }}>Total</p>
                    <p className="gg-tabular" style={{ fontSize: 22, color: 'var(--gg-accent)', fontWeight: 800 }}>{fmtMoney(stats.totalBalance)}</p>
                  </div>
                </div>
              </div>
            </div>

            <button onClick={() => navigate('/erp/finance')} className="gg-btn-secondary w-full justify-center mt-5">
              Open Finance <ChevronRight size={13} />
            </button>
          </div>

          {/* AI insights */}
          <div
            className="gg-card p-6 relative overflow-hidden"
            data-testid="ai-insights"
            style={{
              background: 'linear-gradient(135deg, #0A1F3C 0%, #16324F 100%)',
              color: '#FFFFFF',
              borderColor: 'transparent',
            }}
          >
            <div
              className="absolute -top-10 -end-10 w-32 h-32 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(201,162,39,0.5), transparent 70%)', filter: 'blur(30px)' }}
            />
            <div className="flex items-center gap-2 mb-3 relative">
              <Sparkles size={14} style={{ color: '#E2C15B' }} />
              <p className="gg-eyebrow" style={{ color: '#E2C15B' }}>AI Insights</p>
            </div>
            <ul className="space-y-3 relative">
              {insights.map((i, idx) => (
                <li key={idx} className="text-sm flex items-start gap-2" style={{ color: '#F5F5F7', lineHeight: 1.5 }}>
                  <Circle size={6} fill="currentColor" className="mt-1.5 shrink-0" style={{ color: '#E2C15B' }} />
                  <span>{i.text}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/erp/shipments')}
              className="w-full justify-center mt-5 rounded-xl py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(180deg, #C9A227, #A9861D)',
                color: '#FFFFFF',
                boxShadow: '0 8px 24px -6px rgba(201,162,39,0.5)',
              }}
              data-testid="review-shipments-btn"
            >
              <Ship size={13} className="inline me-1" /> Review shipments
            </button>
          </div>
        </div>
      </section>

      {/* Quick jump strip */}
      <section className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Customers', value: customers.length, action: '/erp/customers', icon: Users },
          { label: 'Approvals', value: 'View', action: '/erp/approvals', icon: FileCheck2, small: true },
          { label: 'Reports', value: 'View', action: '/erp/reports', icon: FileCheck2, small: true },
          { label: 'Settings', value: 'Manage', action: '/erp/settings', icon: Landmark, small: true },
        ].map((s) => (
          <motion.button
            key={s.label}
            onClick={() => navigate(s.action)}
            whileHover={{ y: -3 }}
            className="text-start gg-card gg-card-hover p-5"
            data-testid={`jump-${s.label.toLowerCase()}`}
          >
            <span className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: 'var(--gg-accent-soft)', color: 'var(--gg-accent)' }}>
              <s.icon size={15} />
            </span>
            <p className="gg-eyebrow">{s.label}</p>
            <p className="mt-1 gg-tabular" style={{ fontSize: s.small ? 15 : 24, color: 'var(--gg-text)', fontWeight: 700 }}>
              {s.value}
            </p>
          </motion.button>
        ))}
      </section>

      {/* Footer eyebrow */}
      <footer className="mt-16 pt-8 flex items-center justify-between" style={{ borderTop: '1px solid var(--gg-border)' }}>
        <p className="gg-eyebrow">Gerizim · Customs Clearance</p>
        <p className="text-[11px] gg-mono" style={{ color: 'var(--gg-text-mute)' }}>v2.0 · Navy & Gold</p>
      </footer>
    </ErpShell>
  );
}

function TrackingBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    api.get('/tracking/alerts').then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (dismissed || !data || !data.alerts || data.alerts.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 rounded-2xl p-5"
      style={{ background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.3)' }}
      data-testid="tracking-banner"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(255,149,0,0.15)', color: '#FF9500' }}>
          <Truck size={16} />
        </span>
        <div className="flex-1 min-w-[220px]">
          <p className="font-bold text-sm" style={{ color: '#B26A00' }} data-testid="tracking-banner-title">
            {data.count} shipment{data.count > 1 ? 's are' : ' is'} arriving soon or past ETA
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.alerts.slice(0, 6).map((a) => (
              <span key={a.id} className={`gg-chip gg-chip-${a.displayStatus === 'OVERDUE' ? 'danger' : 'warning'} gg-tabular`} data-testid={`tracking-alert-${a.id}`}>
                {a.customerName || a.trackingNumber || 'Shipment'} · {a.carrier || '—'} · {a.daysToEta < 0 ? `${Math.abs(a.daysToEta)}d late` : a.daysToEta === 0 ? 'today' : `in ${a.daysToEta}d`}
              </span>
            ))}
            {data.alerts.length > 6 && <span className="gg-chip gg-chip-neutral">+{data.alerts.length - 6} more</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/erp/tracking')} className="gg-btn-secondary" style={{ height: 34 }} data-testid="tracking-banner-open">
            View tracking <ChevronRight size={13} />
          </button>
          <button onClick={() => setDismissed(true)} className="gg-btn-ghost" style={{ height: 34 }} data-testid="tracking-banner-dismiss">
            Dismiss
          </button>
        </div>
      </div>
    </motion.section>
  );
}

function OverdueBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    api.get('/overdue-summary').then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (dismissed || !data || !data.overdue || data.overdue.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 rounded-2xl p-5"
      style={{ background: 'rgba(255,59,48,0.07)', border: '1px solid rgba(255,59,48,0.25)' }}
      data-testid="overdue-banner"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(255,59,48,0.15)', color: 'var(--gg-danger)' }}>
          <AlertTriangle size={16} />
        </span>
        <div className="flex-1 min-w-[220px]">
          <p className="font-bold text-sm" style={{ color: 'var(--gg-danger)' }} data-testid="overdue-banner-title">
            {data.overdue.length} customer{data.overdue.length > 1 ? 's have' : ' has'} payments overdue past 30 days
            {data.totalDue > 0 && <span> — ₪{data.totalDue.toLocaleString()} outstanding</span>}
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.overdue.slice(0, 6).map((o) => (
              <span key={o.customerName} className="gg-chip gg-chip-danger gg-tabular" data-testid={`overdue-item-${o.customerName}`}>
                {o.customerName} · {o.amountDue > 0 ? `₪${o.amountDue.toLocaleString()}` : `${o.count} record${o.count > 1 ? 's' : ''}`} · {o.maxDaysOverdue}d late
              </span>
            ))}
            {data.overdue.length > 6 && (
              <span className="gg-chip gg-chip-neutral">+{data.overdue.length - 6} more</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/erp/shipments')} className="gg-btn-secondary" style={{ height: 34 }} data-testid="overdue-banner-open">
            Review <ChevronRight size={13} />
          </button>
          <button onClick={() => setDismissed(true)} className="gg-btn-ghost" style={{ height: 34 }} data-testid="overdue-banner-dismiss">
            Dismiss
          </button>
        </div>
      </div>
    </motion.section>
  );
}
